import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";

export type ArtworkGalleryImage = {
  url: string;
  alt: string;
  label?: string;
};

function imageMatchesSrc(img: HTMLImageElement, src: string): boolean {
  if (img.getAttribute("src") === src) return true;
  try {
    return img.src === new URL(src, window.location.origin).href;
  } catch {
    return false;
  }
}

function useGalleryNavigation(count: number) {
  const [activeIndex, setActiveIndex] = useState(0);

  const goTo = useCallback(
    (index: number) => {
      if (count <= 0) return;
      setActiveIndex(((index % count) + count) % count);
    },
    [count],
  );

  const goPrev = useCallback(() => {
    goTo(activeIndex - 1);
  }, [activeIndex, goTo]);

  const goNext = useCallback(() => {
    goTo(activeIndex + 1);
  }, [activeIndex, goTo]);

  return { activeIndex, goTo, goPrev, goNext };
}

export { useGalleryNavigation };

function useImageLoaded(src: string | undefined) {
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  const markLoaded = useCallback((img: HTMLImageElement) => {
    if (!src || !imageMatchesSrc(img, src)) return;
    setLoaded(true);
  }, [src]);

  useEffect(() => {
    setLoaded(false);
    const img = imgRef.current;
    if (img && src && imageMatchesSrc(img, src) && img.complete && img.naturalWidth > 0) {
      setLoaded(true);
    }
  }, [src]);

  const onLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      markLoaded(event.currentTarget);
    },
    [markLoaded],
  );

  const onError = useCallback(() => {
    setLoaded(true);
  }, []);

  return { imgRef, loaded, onLoad, onError };
}

type ArtworkCardGalleryProps = {
  images: ArtworkGalleryImage[];
  aspectClass?: string;
  objectFit?: "cover" | "contain";
  imgClassName?: string;
  className?: string;
};

/** Compact in-card gallery with prev/next — arrow clicks do not bubble to parent. */
export function ArtworkCardGallery({
  images,
  aspectClass = "aspect-[4/5]",
  objectFit = "cover",
  imgClassName = "",
  className = "",
}: ArtworkCardGalleryProps) {
  const { t } = useTranslation();
  const count = images.length;
  const { activeIndex, goPrev, goNext } = useGalleryNavigation(count);
  const active = images[activeIndex] ?? images[0];
  const { imgRef, loaded, onLoad, onError } = useImageLoaded(active?.url);

  if (!active) return null;

  const showNav = count > 1;

  return (
    <div
      className={`relative w-full overflow-hidden bg-canvas-100 ${aspectClass} ${className}`}
    >
      {!loaded && (
        <div className="art-image-skeleton absolute inset-0" aria-hidden />
      )}
      <img
        key={active.url}
        ref={imgRef}
        src={active.url}
        alt={active.alt}
        loading="lazy"
        decoding="async"
        onLoad={onLoad}
        onError={onError}
        className={`absolute inset-0 z-[1] h-full w-full ${
          objectFit === "contain" ? "object-contain" : "object-cover"
        } ${imgClassName}`}
      />

      {showNav && (
        <>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              goPrev();
            }}
            aria-label={t("common.prev")}
            className="absolute start-2 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border-0 bg-white/90 text-canvas-800 opacity-100 shadow ring-1 ring-hungru-gold/25 transition hover:bg-white hover:ring-hungru-gold/40 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
          >
            <HiChevronLeft className="h-4 w-4 rtl:rotate-180" />
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              goNext();
            }}
            aria-label={t("common.next")}
            className="absolute end-2 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border-0 bg-white/90 text-canvas-800 opacity-100 shadow ring-1 ring-hungru-gold/25 transition hover:bg-white hover:ring-hungru-gold/40 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100"
          >
            <HiChevronRight className="h-4 w-4 rtl:rotate-180" />
          </button>
          <span className="pointer-events-none absolute end-2 top-2 z-10 rounded-full bg-canvas-950/50 px-2 py-0.5 text-[10px] font-medium text-white">
            {activeIndex + 1}/{count}
          </span>
        </>
      )}
    </div>
  );
}

export function ArtworkImageGallery({
  images,
  className = "",
}: {
  images: ArtworkGalleryImage[];
  className?: string;
}) {
  const { t } = useTranslation();
  const count = images.length;
  const { activeIndex, goTo, goPrev, goNext } = useGalleryNavigation(count);
  const active = images[activeIndex] ?? images[0];
  const { imgRef, loaded, onLoad, onError } = useImageLoaded(active?.url);

  useEffect(() => {
    if (count <= 1) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") goPrev();
      if (event.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [count, goPrev, goNext]);

  useEffect(() => {
    if (count <= 1) return;
    for (const offset of [-1, 1]) {
      const neighbor = images[activeIndex + offset];
      if (!neighbor) continue;
      const preload = new Image();
      preload.src = neighbor.url;
    }
  }, [activeIndex, count, images]);

  if (!active) return null;

  const showNav = count > 1;

  return (
    <div className={className}>
      <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-canvas-200/60">
        <div className="relative flex min-h-[16rem] w-full items-center justify-center bg-gradient-to-b from-canvas-50 via-white to-canvas-100 sm:min-h-[20rem] lg:min-h-[28rem]">
          <div className="relative h-[min(52vh,20rem)] w-full sm:h-[min(58vh,26rem)] lg:h-[min(64vh,34rem)]">
            {!loaded && (
              <div className="art-image-skeleton absolute inset-0" aria-hidden />
            )}

            <img
              key={active.url}
              ref={imgRef}
              src={active.url}
              alt={active.alt}
              loading={activeIndex === 0 ? "eager" : "lazy"}
              decoding="async"
              onLoad={onLoad}
              onError={onError}
              className="absolute inset-0 z-[1] h-full w-full object-contain p-3 sm:p-5"
            />

            {showNav && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  aria-label={t("common.prev")}
                  className="absolute start-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border-0 bg-white/95 text-canvas-800 shadow-md ring-1 ring-hungru-gold/25 active:scale-95 sm:start-3 sm:h-10 sm:w-10"
                >
                  <HiChevronLeft className="h-5 w-5 rtl:rotate-180" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  aria-label={t("common.next")}
                  className="absolute end-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border-0 bg-white/95 text-canvas-800 shadow-md ring-1 ring-hungru-gold/25 active:scale-95 sm:end-3 sm:h-10 sm:w-10"
                >
                  <HiChevronRight className="h-5 w-5 rtl:rotate-180" />
                </button>

                <div className="pointer-events-none absolute end-2 top-2 z-10 rounded-full bg-canvas-950/60 px-2.5 py-0.5 text-[10px] font-medium tracking-wide text-white backdrop-blur-sm sm:end-3 sm:top-3 sm:px-3 sm:py-1 sm:text-[11px]">
                  {activeIndex + 1} / {count}
                </div>

                {active.label && (
                  <p className="pointer-events-none absolute bottom-2 start-1/2 z-10 max-w-[90%] -translate-x-1/2 truncate rounded-full bg-white/95 px-2.5 py-0.5 text-[9px] font-medium uppercase tracking-[0.12em] text-canvas-600 shadow-sm ring-1 ring-hungru-gold/20 sm:bottom-3 sm:px-3 sm:py-1 sm:text-[10px]">
                    {active.label}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {showNav && (
        <div className="mt-3 sm:mt-4">
          <p className="mb-2 text-[10px] font-medium uppercase tracking-[0.16em] text-canvas-500 sm:hidden">
            {t("canvas.specs.galleryViews")}
          </p>
          <div className="flex snap-x snap-mandatory gap-2 overflow-x-auto overscroll-x-contain pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {images.map((image, index) => (
              <button
                key={`${image.url}-${index}`}
                type="button"
                onClick={() => goTo(index)}
                aria-label={
                  image.label ??
                  t("canvas.specs.viewImage", { index: index + 1 })
                }
                aria-current={index === activeIndex ? "true" : undefined}
                className={`shrink-0 snap-center overflow-hidden rounded-lg border-0 p-0 transition sm:rounded-xl ${
                  index === activeIndex
                    ? "ring-2 ring-hungru-gold/45 shadow-[0_4px_20px_rgba(205,182,127,0.35)]"
                    : "ring-1 ring-canvas-200/80 opacity-80 hover:opacity-100 hover:ring-hungru-gold/25"
                }`}
              >
                <span className="relative block h-14 w-14 sm:h-16 sm:w-16">
                  <img
                    src={image.url}
                    alt={image.alt}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
